import type { LyricCue } from '../types';
import { normalizeLyricText } from './lyricsApi';

function countMap(lines: string[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const line of lines) {
    if (!line) continue;
    map.set(line, (map.get(line) ?? 0) + 1);
  }
  return map;
}

function repeatedBlockScore(lines: string[], blockLen: number): Map<string, number> {
  const scores = new Map<string, number>();
  for (let i = 0; i <= lines.length - blockLen; i++) {
    const block = lines.slice(i, i + blockLen).join('|');
    if (block.length < 6) continue;
    scores.set(block, (scores.get(block) ?? 0) + 1);
  }
  return scores;
}

export function detectHooks(lyrics: LyricCue[], durationMs: number): LyricCue[] {
  if (lyrics.length === 0) return [];

  const normalized = lyrics.map((l) => l.normalizedText || normalizeLyricText(l.text));
  const lineCounts = countMap(normalized);

  const chorusWindowStart = durationMs * 0.25;
  const chorusWindowEnd = durationMs * 0.75;

  const blockScores = new Map<string, number>();
  for (let blockLen = 2; blockLen <= 8; blockLen++) {
    const scores = repeatedBlockScore(normalized, blockLen);
    for (const [block, count] of scores) {
      if (count >= 2) blockScores.set(block, Math.max(blockScores.get(block) ?? 0, count * blockLen));
    }
  }

  return lyrics.map((cue, idx) => {
    const norm = normalized[idx];
    const repeatCount = lineCounts.get(norm) ?? 1;
    const isRepeated = repeatCount >= 2;
    const phraseWords = norm.split(' ').filter(Boolean);
    const isShortPhrase = phraseWords.length >= 2 && phraseWords.length <= 8;

    let weight = 1;
    if (isRepeated) weight += repeatCount * 1.5;
    if (isShortPhrase) weight += 0.8;

    for (const [block, score] of blockScores) {
      if (block.includes(norm) && norm.length >= 4) weight += score * 0.35;
    }

    const mid = (cue.startMs + cue.endMs) / 2;
    if (mid >= chorusWindowStart && mid <= chorusWindowEnd) weight += 1.2;

    const isLikelyHook = weight >= 3.5 && isShortPhrase && (isRepeated || phraseWords.length <= 5);

    return {
      ...cue,
      weight,
      isRepeated,
      isLikelyHook,
    };
  });
}

export function getTopHooks(lyrics: LyricCue[], limit = 6): LyricCue[] {
  const hooks = lyrics.filter((l) => l.isLikelyHook);
  const unique = new Map<string, LyricCue>();
  for (const hook of hooks) {
    const key = hook.normalizedText;
    const existing = unique.get(key);
    if (!existing || hook.weight > existing.weight) unique.set(key, hook);
  }
  return [...unique.values()].sort((a, b) => b.weight - a.weight).slice(0, limit);
}
